import { div, h1, h2, span, button, img, select, option, input, textarea, a, label, fnlink, fnstate } from '@srfnstack/fntags'
import { fnfetch } from '../fetch.js'
import { showNotification } from '../state.js'

// The Pin Queue: Pinterest pin drafts PINTEREST_AUTOPIN wrote. Pinterest's
// developer guidelines require the user to choose each scheduled pin, so a
// draft is only ever published after the operator approves it here (or on its
// Discord card). One card per draft: the image, the board, the editable title
// and description, the link, and Approve / Reject.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// formatSlot renders a slot as "Oct 7 14:20 UTC" (same as the Discord card).
export const formatSlot = (value) => {
  const d = new Date(value)
  if (!value || Number.isNaN(d.getTime())) return ''
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${hh}:${mm} UTC`
}

const STATUS_OPTIONS = [
  ['draft', 'Waiting for review'],
  ['approved', 'Approved'],
  ['published', 'Published'],
  ['failed', 'Failed'],
  ['rejected', 'Rejected'],
  ['all', 'Everything']
]

const TITLE_MAX = 100
const DESCRIPTION_MAX = 500

// draftFacts says how the job engine made a draft: the image (raw mockup or a
// fresh pin layout), who wrote the copy, and the critic's verdict.
const draftFacts = (d) => {
  const image = d.creative_layout ? `fresh pin image (${d.creative_layout} layout)` : 'mockup image'
  const copy = d.copy_source === 'ai' ? 'AI copy' : 'template copy'
  const critic = d.critic_verdict === 'pass'
    ? 'critic passed'
    : d.critic_verdict === 'reject'
      ? 'critic rejected'
      : d.critic_verdict === 'skipped' ? 'critic could not check' : 'critic off'
  return `${image} · ${copy} · ${critic}`
}

const STATUS_BADGE = {
  draft: 'badge-ghost',
  approved: 'badge-success',
  published: 'badge-primary',
  failed: 'badge-error',
  rejected: 'badge-neutral'
}

// proposalFacts is the one-line summary of a board proposal: its kind, the
// expected peak for a seasonal board, what it fits, and how fast it grows.
export const proposalFacts = (p) => {
  const parts = [p.kind]
  if (p.peak_date) {
    const d = new Date(`${p.peak_date}T00:00:00Z`)
    if (!Number.isNaN(d.getTime())) parts.push(`peak ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`)
  }
  if (p.tribes?.length) parts.push(`tribes ${p.tribes.join(', ')}`)
  if (p.rooms?.length) parts.push(`rooms ${p.rooms.join(', ')}`)
  if (p.pct_growth_mom !== null && p.pct_growth_mom !== undefined) parts.push(`${p.pct_growth_mom >= 0 ? '+' : ''}${p.pct_growth_mom}% month`)
  return parts.join(' · ')
}

const PROPOSAL_BADGE = {
  proposed: 'badge-warning',
  approved: 'badge-success',
  rejected: 'badge-neutral',
  retired: 'badge-ghost'
}

// BoardProposals: boards BOARD_EXPLORE proposed from Pinterest trends. A
// proposal changes nothing until approved here or on its Discord card; an
// approved board gets a 10-pin trial and is created by its first pin.
export const BoardProposals = () => {
  const proposals = fnstate([], p => p.id)

  const load = async () => {
    try {
      const res = await fetch('/api/pinterest/board-proposals')
      const data = res.ok ? await res.json() : []
      proposals(Array.isArray(data) ? data : [])
    } catch {
      proposals([])
    }
  }
  load()

  const proposalRow = (pState) => {
    const p = pState()
    const busy = fnstate(false)
    const error = fnstate('')
    // A decided proposal leaves the waiting list at once: a rejected one is
    // dropped, an approved one is reloaded into the approved group (with its
    // trial progress). A failure stays on the row with the reason.
    const decide = async (decision) => {
      busy(true)
      error('')
      try {
        const res = await fnfetch(`/api/pinterest/board-proposals/${p.id}/${decision}`, { method: 'POST' })
        if (res.ok) {
          const data = await res.json()
          if (decision === 'reject') {
            proposals(proposals().filter(x => x.id !== p.id))
          } else {
            pState.assign({ ...(data.proposal || {}), status: data.proposal?.status || 'approved' })
            load()
          }
        } else {
          const e = await res.json().catch(() => ({}))
          error(`Could not ${decision}: ${e.error || res.status}`)
          showNotification(`Failed to ${decision}: ${e.error || res.status}`, 'error')
        }
      } catch (err) {
        error(`Could not ${decision}: ${err.message}`)
      }
      busy(false)
    }
    return div({ class: 'flex flex-wrap items-center gap-2 border border-base-300 rounded-box p-3 bg-base-100' },
      span({ class: 'font-semibold text-sm' }, p.board_name),
      pState.bindAs(cur => span({ class: `badge badge-sm ${PROPOSAL_BADGE[cur.status] || 'badge-ghost'}` }, cur.status)),
      span({ class: 'text-xs opacity-60' }, proposalFacts(p)),
      p.status === 'approved' && p.trial_pinned !== null
        ? span({ class: 'text-xs opacity-60' }, `trial ${Math.min(p.trial_pinned, p.trial_pins)} of ${p.trial_pins} pins`)
        : '',
      p.reason ? span({ class: 'text-xs opacity-70 w-full' }, p.reason) : '',
      p.retire_reason ? span({ class: 'text-xs opacity-70 w-full' }, `Retired: ${p.retire_reason}`) : '',
      error.bindAs(e => e ? span({ class: 'text-xs text-error w-full', role: 'alert' }, e) : span()),
      pState.bindAs(cur => cur.status === 'proposed'
        ? div({ class: 'flex gap-2 ml-auto' },
          button({ class: 'btn btn-xs btn-ghost', disabled: busy.bindAttr(b => b), onclick: () => decide('reject') }, 'Reject board'),
          button({ class: 'btn btn-xs btn-success', disabled: busy.bindAttr(b => b), onclick: () => decide('approve') }, 'Approve board')
        )
        : span()
      )
    )
  }

  return div({ class: 'flex flex-col gap-2' },
    proposals.bindAs(list => list.length === 0
      ? span()
      : div({ class: 'flex flex-col gap-1' },
        h2({ class: 'text-lg font-bold' }, 'Board proposals'),
        span({ class: 'text-sm opacity-70' },
          'New boards suggested from Pinterest trends, at most two a month. Approving one gives it 10 pins over 3 weeks with designs that fit; after 8 weeks a board in the bottom quarter is retired (its pins stay).')
      )
    ),
    proposals.bindChildren(div({ class: 'flex flex-col gap-2' }), proposalRow)
  )
}

// pinCountLabel is "1 pin" or "8 pins".
export const pinCountLabel = (n) => `${n} pin${n === 1 ? '' : 's'}`

export const PinQueue = () => {
  const status = fnstate('draft')
  const drafts = fnstate([], d => d.id)
  const loading = fnstate(true)
  const bulkBusy = fnstate(false)
  // Each card registers how to read its unsaved title/description edits, so
  // "Approve all shown" sends what the operator sees on screen.
  const editsById = new Map()

  const load = async () => {
    loading(true)
    try {
      const res = await fetch(`/api/pinterest/drafts?status=${status()}`)
      drafts(res.ok ? await res.json() : [])
    } catch {
      drafts([])
    }
    loading(false)
  }
  status.subscribe(load)
  load()

  // settle applies a decision's outcome to the list: a card whose new status
  // no longer matches the filter leaves at once; under "Everything" it stays
  // and shows the new status.
  const settle = (dState, id, patch) => {
    if (status() !== 'all' && patch.status !== status()) {
      editsById.delete(id)
      drafts(drafts().filter(x => x.id !== id))
      return true
    }
    dState.assign(patch)
    return false
  }

  const postDecision = async (id, decision, body) => {
    try {
      const res = await fnfetch(`/api/pinterest/drafts/${id}/${decision}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
      const data = await res.json().catch(() => ({}))
      return res.ok ? { ok: true, data } : { ok: false, error: data.error || `HTTP ${res.status}` }
    } catch (err) {
      return { ok: false, error: err.message }
    }
  }

  // Pinterest's guidelines require the operator to choose each pin, so this
  // approves exactly the waiting drafts rendered right now (a snapshot taken
  // at the click, confirmed by count), one approve call per draft. Drafts
  // written after the page loaded are not on screen and are never touched.
  const approveAllShown = async () => {
    const shown = drafts().filter(d => d.status === 'draft')
    if (shown.length === 0 || bulkBusy()) return
    if (!window.confirm(`Approve ${pinCountLabel(shown.length)}?`)) return
    bulkBusy(true)
    let approved = 0
    const failures = []
    for (const d of shown) {
      const res = await postDecision(d.id, 'approve', editsById.get(d.id)?.() || {})
      if (res.ok) {
        approved++
        if (status() !== 'all' && status() !== 'approved') {
          editsById.delete(d.id)
          drafts(drafts().filter(x => x.id !== d.id))
        }
      } else {
        failures.push(`#${d.id}: ${res.error}`)
      }
    }
    bulkBusy(false)
    if (status() === 'all') load()
    if (failures.length) {
      showNotification(`Approved ${pinCountLabel(approved)}; ${failures.length} failed (${failures.join('; ')})`, 'error')
    } else {
      showNotification(`Approved ${pinCountLabel(approved)}. They go out one at a time at their slots.`, 'success')
    }
  }

  const draftCard = (dState) => {
    const d = dState()
    const title = fnstate(d.title)
    const description = fnstate(d.description)
    const busy = fnstate(false)
    const error = fnstate('')
    const note = fnstate(d.status === 'approved' ? `Approved, publishes around ${formatSlot(d.scheduled_for)}` : '')
    // A failed draft is retried as it was (or rejected); waiting and approved
    // drafts can be edited before approving.
    const editable = ['draft', 'approved'].includes(d.status)
    const failed = d.status === 'failed'

    const editsBody = () => {
      const body = {}
      if (title() !== d.title) body.title = title()
      if (description() !== d.description) body.description = description()
      return body
    }
    if (editable) editsById.set(d.id, editsBody)

    const act = async (decision) => {
      busy(true)
      error('')
      const res = await postDecision(d.id, decision, decision === 'approve' ? editsBody() : {})
      if (res.ok) {
        const newStatus = decision === 'reject' ? 'rejected' : 'approved'
        const gone = settle(dState, d.id, { status: newStatus, title: title(), description: description(), error: null })
        if (!gone) {
          note(newStatus === 'rejected' ? 'Rejected' : `${decision === 'retry' ? 'Re-queued' : 'Approved'}, publishes around ${formatSlot(res.data.publish_around)}`)
        }
      } else {
        const verb = { approve: 'approve', reject: 'reject', retry: 'retry' }[decision]
        error(`Could not ${verb}: ${res.error}`)
        showNotification(`Failed to ${verb}: ${res.error}`, 'error')
      }
      busy(false)
    }

    const counter = (state, max) => state.bindAs(v => span({ class: `text-xs ${[...(v || '')].length > max ? 'text-error' : 'opacity-50'}` }, `${[...(v || '')].length}/${max}`))

    const actionButton = (text, cls, onclick) => button({ class: `btn btn-sm ${cls}`, disabled: busy.bindAttr(b => b), onclick }, text)

    return div({ class: 'card bg-base-100 border border-base-300 shadow-sm' },
      d.image_url
        ? img({ src: d.image_url, alt: d.alt_text || `pin draft ${d.id}`, class: 'w-full aspect-square object-contain bg-base-200 rounded-t-box', loading: 'lazy' })
        : div({ class: 'w-full aspect-square bg-base-200 rounded-t-box' }),
      div({ class: 'card-body p-3 md:p-4 gap-2' },
        div({ class: 'flex flex-wrap items-center gap-2' },
          span({ class: 'badge badge-outline badge-sm' }, d.board_name),
          dState.bindAs(cur => span({ class: `badge badge-sm ${STATUS_BADGE[cur.status] || 'badge-ghost'}` }, cur.status)),
          d.scheduled_for && d.status !== 'published'
            ? span({ class: 'text-xs opacity-60' }, `slot ${formatSlot(d.scheduled_for)}`)
            : ''
        ),
        editable
          ? div({ class: 'flex flex-col gap-2' },
            label({ class: 'form-control w-full' },
              div({ class: 'flex justify-between' }, span({ class: 'label-text text-xs font-bold uppercase opacity-50' }, 'Title'), counter(title, TITLE_MAX)),
              input({
                type: 'text',
                name: 'title',
                class: 'input input-bordered input-sm w-full',
                value: title.bindAttr(),
                oninput: e => title(e.target.value)
              })
            ),
            label({ class: 'form-control w-full' },
              div({ class: 'flex justify-between' }, span({ class: 'label-text text-xs font-bold uppercase opacity-50' }, 'Description'), counter(description, DESCRIPTION_MAX)),
              textarea({
                name: 'description',
                rows: '5',
                class: 'textarea textarea-bordered textarea-sm w-full leading-snug',
                value: description.bindAttr(),
                oninput: e => description(e.target.value)
              })
            )
          )
          : div({ class: 'flex flex-col gap-1' },
            span({ class: 'font-semibold text-sm' }, d.title),
            span({ class: 'text-sm whitespace-pre-line opacity-80' }, d.description)
          ),
        span({ class: 'text-xs opacity-60' }, draftFacts(d)),
        d.critic_verdict === 'skipped' && d.critic_reasons
          ? span({ class: 'text-xs opacity-60' }, `Critic could not check: ${d.critic_reasons}`)
          : '',
        a({ href: d.link, target: '_blank', rel: 'noopener', class: 'link text-xs break-all opacity-70' }, d.link),
        dState.bindAs(cur => cur.error ? span({ class: 'text-xs text-error' }, cur.error) : span()),
        d.pin_url ? a({ href: d.pin_url, target: '_blank', rel: 'noopener', class: 'link link-primary text-sm' }, 'View pin') : '',
        note.bindAs(n => n ? span({ class: 'text-sm' }, n) : span()),
        error.bindAs(e => e ? span({ class: 'text-sm text-error', role: 'alert' }, e) : span()),
        editable
          ? div({ class: 'card-actions justify-end gap-2 mt-1' },
            actionButton('Reject', 'btn-ghost', () => act('reject')),
            actionButton('Approve', 'btn-success', () => act('approve'))
          )
          : failed
            ? div({ class: 'card-actions justify-end gap-2 mt-1' },
              actionButton('Reject', 'btn-ghost', () => act('reject')),
              actionButton('Retry', 'btn-warning', () => act('retry'))
            )
            : ''
      )
    )
  }

  return div({ class: 'p-4 md:p-6 flex flex-col gap-4' },
    div({ class: 'flex flex-wrap items-center gap-3' },
      h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Pin Queue'),
      drafts.bindAs(list => span({ class: 'badge badge-ghost font-mono' }, `${list.length}`)),
      div({ class: 'flex flex-wrap gap-2 ml-auto items-center' },
        fnlink({ to: '/pinterest-stats', class: 'link text-sm' }, 'Stats'),
        fnlink({ to: '/settings', class: 'link text-sm' }, 'Settings'),
        drafts.bindAs(list => {
          const waiting = list.filter(d => d.status === 'draft').length
          return status() === 'draft' && waiting > 0
            ? button({
              class: 'btn btn-sm btn-success btn-outline',
              disabled: bulkBusy.bindAttr(b => b),
              onclick: approveAllShown
            }, `Approve all shown (${waiting})`)
            : span()
        }),
        select({
          class: 'select select-bordered select-sm',
          value: status.bindAttr(),
          onchange: e => status(e.target.value)
        }, ...STATUS_OPTIONS.map(([v, text]) => option({ value: v }, text)))
      )
    ),
    span({ class: 'text-sm opacity-70' },
      'Nothing here is posted to Pinterest until you approve it. Approved pins go out one at a time at their slot, a few hours apart. A failed pin keeps its listing out of new drafts until you retry or reject it.'),
    BoardProposals(),
    loading.bindAs(l => l
      ? span({ class: 'loading loading-dots loading-md' })
      : drafts.bindAs(list => list.length === 0
        ? div({ class: 'opacity-60 text-sm' }, 'No pins here. Drafts appear once Pin Queue drafting is on in Settings.')
        : span()
      )
    ),
    drafts.bindChildren(div({ class: 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4' }), draftCard)
  )
}
